import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;
const API = process.env.REACT_APP_API_URL.replace('/v0', '');

const CheckoutForm = ({ amount, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    setError(null);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message);
      setPaying(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <PaymentElement />
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={paying || !stripe}
        className="h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {paying ? 'Processing...' : `Pay $${amount.toFixed(2)} CAD`}
      </button>
    </form>
  );
};

const Pay = () => {
  const { tenantDomain, invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const invoiceRes = await fetch(`${API}/pay/${tenantDomain}/${invoiceId}`);
        if (!invoiceRes.ok) throw new Error('not_found');
        const invoiceData = await invoiceRes.json();
        setInvoice(invoiceData);

        if (invoiceData.status === 'sent') {
          const secretRes = await fetch(`${API}/pay/${tenantDomain}/${invoiceId}/client-secret`);
          if (secretRes.ok) {
            const { clientSecret: cs } = await secretRes.json();
            setClientSecret(cs);
          }
        }
      } catch (err) {
        setError(err.message === 'not_found' ? 'Invoice not found.' : 'Unable to load invoice.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantDomain, invoiceId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl border shadow-sm p-8 max-w-md w-full text-center">
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (invoice?.status === 'paid' || success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl border shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold mb-1">Payment complete</h1>
          <p className="text-sm text-gray-500">
            {success ? 'Thank you — your payment has been processed.' : 'This invoice has already been paid.'}
          </p>
        </div>
      </div>
    );
  }

  if (invoice?.status !== 'sent') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl border shadow-sm p-8 max-w-md w-full text-center">
          <p className="text-sm text-gray-500">This invoice is not available for payment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border shadow-sm p-8 max-w-md w-full">
        <div className="mb-6">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Invoice from</p>
          <h1 className="text-xl font-semibold">{invoice.trainerName}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{invoice.tenantName}</p>
        </div>

        <div className="rounded-lg bg-gray-50 border p-4 mb-6 flex justify-between items-start">
          <div>
            <p className="text-sm font-medium">{invoice.packageName}</p>
            <p className="text-xs text-gray-500 mt-0.5">For {invoice.clientName}</p>
          </div>
          <p className="text-lg font-semibold">${invoice.amount.toFixed(2)}</p>
        </div>

        {clientSecret ? (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm amount={invoice.amount} onSuccess={() => setSuccess(true)} />
          </Elements>
        ) : (
          <p className="text-sm text-gray-500 text-center">Payment unavailable. Please contact your trainer.</p>
        )}

        <p className="text-xs text-gray-400 text-center mt-4">
          Payments are processed securely by Stripe.
        </p>
      </div>
    </div>
  );
};

export default Pay;
